/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';

const ImageComponent = React.lazy(() => import('./ImageComponent'));

export interface ImagePayload {
  altText: string;
  caption?: LexicalEditor;
  height?: number | 'inherit' | '100%' | 'auto';
  key?: NodeKey;
  maxWidth?: number;
  showCaption?: boolean;
  src: string;
  width?: number | 'inherit' | '100%' | 'auto';
  captionsEnabled?: boolean;
  manualId?: string;
  attachmentId?: string;
}

function isGoogleDocCheckboxImg(img: HTMLImageElement): boolean {
  return (
    img.parentElement != null &&
    img.parentElement.tagName === 'LI' &&
    img.previousSibling === null &&
    img.getAttribute('aria-roledescription') === 'checkbox'
  );
}

function $convertImageElement(domNode: Node): null | DOMConversionOutput {
  const img = domNode as HTMLImageElement;
  const attachmentId = img.getAttribute('data-attachment-id') ?? undefined;
  const manualId = img.getAttribute('data-manual-id') ?? undefined;
  const widthAttr = img.getAttribute('width');
  const heightAttr = img.getAttribute('height');
  const width = widthAttr ? parseInt(widthAttr, 10) : 300;
  const height = heightAttr ? parseInt(heightAttr, 10) : 200;

  const { alt: altText, src } = img;
  const node = $createImageNode({
    altText,
    src,
    width,
    height,
    manualId,
    attachmentId,
  });

  return { node };
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    caption: SerializedEditor;
    height?: number | 'inherit' | '100%' | 'auto';
    maxWidth: number;
    showCaption: boolean;
    src: string;
    width?: number | 'inherit' | '100%' | 'auto';
    manualId?: string;
    attachmentId?: string;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width: 'inherit' | '100%' | 'auto' | number;
  __height: 'inherit' | '100%' | 'auto' | number;
  __maxWidth: number;
  __showCaption: boolean;
  __caption: LexicalEditor;
  __captionsEnabled: boolean;
  __manualId?: string;
  __attachmentId?: string;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__manualId,
      node.__attachmentId,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, height, width, maxWidth, caption, src, showCaption, manualId, attachmentId } = serializedNode;
    const node = $createImageNode({
      altText,
      height,
      maxWidth,
      showCaption,
      src,
      width,
      manualId,
      attachmentId,
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    if (typeof this.__width === 'number') {
      element.setAttribute('width', this.__width.toString());
    } else {
      element.style.width = this.__width;
    }
    if (typeof this.__height === 'number') {
      element.setAttribute('height', this.__height.toString());
    } else {
      element.style.height = this.__height;
    }
    if (this.__attachmentId) {
      element.setAttribute('data-attachment-id', this.__attachmentId);
    }
    if (this.__manualId) {
      element.setAttribute('data-manual-id', this.__manualId);
    }
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: Node) => ({
        conversion: $convertImageElement,
        priority: 2,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    maxWidth: number,
    width?: 'inherit' | '100%' | 'auto' | number,
    height?: 'inherit' | '100%' | 'auto' | number,
    showCaption?: boolean,
    caption?: LexicalEditor,
    captionsEnabled?: boolean,
    manualId?: string,
    attachmentId?: string,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__maxWidth = maxWidth;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption =
      caption ||
      createEditor({
        nodes: [],
      });
    this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
    this.__manualId = manualId;
    this.__attachmentId = attachmentId;
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height,
      maxWidth: this.__maxWidth,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width,
      manualId: this.__manualId,
      attachmentId: this.__attachmentId,
    };
  }

  setWidthAndHeight(width: 'inherit' | '100%' | 'auto' | number, height: 'inherit' | '100%' | 'auto' | number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  setShowCaption(showCaption: boolean): void {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }
  setSrc(newSrc: string): void {
    const writable = this.getWritable();
    writable.__src = newSrc;
  }

  setManualId(newManualId: string): void {
    const writable = this.getWritable();
    writable.__manualId = newManualId;
  }
  setAttachmentId(newAttachmentId: string): void {
    const writable = this.getWritable();
    writable.__attachmentId = newAttachmentId;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <ImageComponent
          src={this.__src}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          maxWidth={this.__maxWidth}
          nodeKey={this.getKey()}
          showCaption={false}
          caption={this.__caption}
          captionsEnabled={false}
          resizable={false}
          manualId={this.__manualId}
          attachmentId={this.__attachmentId}
        />
      </Suspense>
    );
  }
}

export function $createImageNode({
  altText,
  height,
  maxWidth = 10000,
  captionsEnabled,
  src,
  width,
  showCaption,
  caption,
  manualId,
  attachmentId,
  key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(
      src,
      altText,
      maxWidth,
      width,
      height,
      showCaption,
      caption,
      captionsEnabled,
      manualId,
      attachmentId,
      key
    )
  );
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
